import React, { useState } from 'react';
import { Linkedin } from 'lucide-react';
import { LinkedInAdsContent } from '../types/ads.types';
import { AD_PLATFORMS } from '../config/adPlatformConfig';
import MediaUploader from '../components/MediaUploader';
import PlatformEditorTabs from '../components/PlatformEditorTabs';

interface BlobUrlManager {
  add: (url: string) => void;
  remove: (url: string) => void;
  cleanup: () => void;
}

interface LinkedInAdsEditorProps {
  content: LinkedInAdsContent;
  isEditing: boolean;
  onChange: (field: keyof LinkedInAdsContent, value: any) => void;
  blobManager?: BlobUrlManager;
  contentId?: number;
  apiBaseUrl?: string;
}

export default function LinkedInAdsEditor({ content, isEditing, onChange, blobManager, contentId, apiBaseUrl }: LinkedInAdsEditorProps) {
  const platform = AD_PLATFORMS.linkedin;
  const [currentIntroTextIndex, setCurrentIntroTextIndex] = useState(0);
  const [currentHeadlineIndex, setCurrentHeadlineIndex] = useState(0);
  const [showAllIntroText, setShowAllIntroText] = useState(false);
  const [showAllHeadlines, setShowAllHeadlines] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [activeAssetTab, setActiveAssetTab] = useState('images');
  const [selectedAdFormats, setSelectedAdFormats] = useState<string[]>(
    content.adFormats || [content.adFormat || 'sponsored_content']
  );

  const tabs = [
    { id: 'settings', label: 'Settings' },
    { id: 'assets', label: 'Assets' },
    { id: 'targeting', label: 'Targeting' },
    { id: 'budget', label: 'Budget' }
  ];

  // Initialize variations if they don't exist
  const introTextVariations = content.introTextVariations || [content.introText || ''];
  const headlineVariations = content.headlineVariations || [content.headline || ''];

  const updateIntroTextVariations = (variations: string[]) => {
    onChange('introTextVariations', variations);
    // Keep main field in sync for backwards compatibility
    onChange('introText', variations[currentIntroTextIndex] || '');
  };

  const updateHeadlineVariations = (variations: string[]) => {
    onChange('headlineVariations', variations);
    // Keep main field in sync for backwards compatibility
    onChange('headline', variations[currentHeadlineIndex] || '');
  };

  const getAssetSubTabs = () => {
    const tabs = [];

    // Always show text tab for LinkedIn
    tabs.push({ id: 'text', label: 'Text' });

    // Images for sponsored content and carousel
    if (selectedAdFormats.some(format =>
      ['sponsored_content', 'carousel', 'single_image'].includes(format)
    )) {
      tabs.push({ id: 'images', label: 'Images' });
    }

    // Videos for video ads and sponsored content
    if (selectedAdFormats.some(format =>
      ['video', 'sponsored_content'].includes(format)
    )) {
      tabs.push({ id: 'videos', label: 'Videos' });
    }

    // Documents for document ads
    if (selectedAdFormats.includes('document')) {
      tabs.push({ id: 'documents', label: 'Documents' });
    }

    return tabs.length > 0 ? tabs : [{ id: 'text', label: 'Text' }];
  };

  const handleAdFormatToggle = (format: string) => {
    const newFormats = selectedAdFormats.includes(format)
      ? selectedAdFormats.filter(f => f !== format)
      : [...selectedAdFormats, format];

    setSelectedAdFormats(newFormats);
    onChange('adFormats', newFormats);

    // Update activeAssetTab if current one is no longer valid
    const validTabs = getAssetSubTabs();
    if (!validTabs.find(t => t.id === activeAssetTab)) {
      setActiveAssetTab(validTabs[0].id);
    }
  };

  return (
    <div className="space-y-6">
      <PlatformEditorTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Linkedin className="w-5 h-5 mr-2 text-blue-700" />
            LinkedIn Campaign Settings
          </h3>

          <div className="space-y-4">
            {/* Campaign Name - Full Width */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
              <input
                type="text"
                value={content.campaignName || ''}
                onChange={(e) => onChange('campaignName', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Enter campaign name..."
              />
            </div>

            {/* Ad Formats - Checkboxes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ad Formats</label>
              <div className="space-y-2">
                {platform.formats.map((format) => {
                  const formatValue = format.toLowerCase().replace(/ /g, '_');
                  return (
                    <label key={formatValue} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedAdFormats.includes(formatValue)}
                        onChange={() => handleAdFormatToggle(formatValue)}
                        disabled={!isEditing}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:bg-gray-100"
                      />
                      <span className="ml-2 text-sm text-gray-700">{format}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Campaign Objective */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Objective</label>
              <select
                value={content.objective || 'brand_awareness'}
                onChange={(e) => onChange('objective', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="brand_awareness">Brand Awareness</option>
                <option value="website_visits">Website Visits</option>
                <option value="engagement">Engagement</option>
                <option value="lead_generation">Lead Generation</option>
                <option value="website_conversions">Website Conversions</option>
                <option value="job_applicants">Job Applicants</option>
              </select>
            </div>

            {/* Company Page */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Page</label>
              <input
                type="text"
                value={content.companyPage || ''}
                onChange={(e) => onChange('companyPage', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Your company LinkedIn page..."
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'assets' && (
        <div className="space-y-6">
          {/* Asset Sub-tabs */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="border-b">
              <div className="flex space-x-1 p-1">
                {getAssetSubTabs().map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveAssetTab(tab.id)}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeAssetTab === tab.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {/* Text Assets */}
              {activeAssetTab === 'text' && (
                <div className="space-y-6">
                  {/* Intro Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Intro Text</label>
                    <textarea
                      value={content.introText || ''}
                      onChange={(e) => onChange('introText', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      rows={3}
                      maxLength={150}
                      placeholder="Enter intro text (up to 150 characters)..."
                    />
                    <div className="text-xs text-gray-500 mt-1">{(content.introText || '').length}/150 characters</div>
                  </div>

                  {/* Headlines */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Headlines</label>
                    <input
                      type="text"
                      value={content.headline || ''}
                      onChange={(e) => onChange('headline', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      maxLength={70}
                      placeholder="Enter headline (up to 70 characters)..."
                    />
                    <div className="text-xs text-gray-500 mt-1">{(content.headline || '').length}/70 characters</div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={content.description || ''}
                      onChange={(e) => onChange('description', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      rows={2}
                      maxLength={100}
                      placeholder="Enter description (up to 100 characters)..."
                    />
                    <div className="text-xs text-gray-500 mt-1">{(content.description || '').length}/100 characters</div>
                  </div>

                  {/* Call to Action */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Call to Action</label>
                    <select
                      value={content.callToAction || 'learn_more'}
                      onChange={(e) => onChange('callToAction', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      <option value="learn_more">Learn More</option>
                      <option value="sign_up">Sign Up</option>
                      <option value="download">Download</option>
                      <option value="get_quote">Get Quote</option>
                      <option value="subscribe">Subscribe</option>
                      <option value="apply">Apply</option>
                      <option value="contact_us">Contact Us</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Images */}
              {activeAssetTab === 'images' && (
                <MediaUploader
                  creatives={content.creatives?.filter(c => c.type === 'image') || []}
                  onCreativesChange={(creatives) => {
                    const otherCreatives = content.creatives?.filter(c => c.type !== 'image') || [];
                    onChange('creatives', [...otherCreatives, ...creatives]);
                  }}
                  disabled={!isEditing}
                  platform="LinkedIn"
                  acceptedTypes={['image/*']}
                  maxFiles={10}
                  blobManager={blobManager}
                  contentId={contentId}
                  apiBaseUrl={apiBaseUrl}
                />
              )}

              {/* Videos */}
              {activeAssetTab === 'videos' && (
                <MediaUploader
                  creatives={content.creatives?.filter(c => c.type === 'video') || []}
                  onCreativesChange={(creatives) => {
                    const otherCreatives = content.creatives?.filter(c => c.type !== 'video') || [];
                    onChange('creatives', [...otherCreatives, ...creatives]);
                  }}
                  disabled={!isEditing}
                  platform="LinkedIn"
                  acceptedTypes={['video/*']}
                  maxFiles={5}
                  blobManager={blobManager}
                  contentId={contentId}
                  apiBaseUrl={apiBaseUrl}
                />
              )}

              {/* Documents */}
              {activeAssetTab === 'documents' && (
                <MediaUploader
                  creatives={content.creatives?.filter(c => c.type === 'document') || []}
                  onCreativesChange={(creatives) => {
                    const otherCreatives = content.creatives?.filter(c => c.type !== 'document') || [];
                    onChange('creatives', [...otherCreatives, ...creatives]);
                  }}
                  disabled={!isEditing}
                  platform="LinkedIn"
                  acceptedTypes={['application/pdf']}
                  maxFiles={1}
                  blobManager={blobManager}
                  contentId={contentId}
                  apiBaseUrl={apiBaseUrl}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'targeting' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Professional Targeting</h3>

          <div className="space-y-4">
            {/* Job Titles */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Titles</label>
              <input
                type="text"
                value={content.jobTitles || ''}
                onChange={(e) => onChange('jobTitles', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="e.g., CEO, Marketing Manager, Software Engineer"
              />
            </div>

            {/* Industries */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industries</label>
              <input
                type="text"
                value={content.industries || ''}
                onChange={(e) => onChange('industries', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="e.g., Technology, Healthcare, Finance"
              />
            </div>

            {/* Company Size */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Size</label>
              <select
                value={content.companySize || 'all'}
                onChange={(e) => onChange('companySize', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="all">All Sizes</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="201-500">201-500 employees</option>
                <option value="501-1000">501-1000 employees</option>
                <option value="1001-5000">1001-5000 employees</option>
                <option value="5001-10000">5001-10000 employees</option>
                <option value="10001+">10001+ employees</option>
              </select>
            </div>

            {/* Locations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Locations</label>
              <input
                type="text"
                value={content.locations || ''}
                onChange={(e) => onChange('locations', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="e.g., United States, New York, San Francisco"
              />
            </div>

            {/* Skills */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Skills</label>
              <input
                type="text"
                value={content.skills || ''}
                onChange={(e) => onChange('skills', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="e.g., Project Management, Python, Digital Marketing"
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'budget' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Budget & Bidding</h3>

          <div className="space-y-4">
            {/* Budget Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget Type</label>
              <select
                value={content.budgetType || 'daily'}
                onChange={(e) => onChange('budgetType', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="daily">Daily Budget</option>
                <option value="total">Total Campaign Budget</option>
              </select>
            </div>

            {/* Budget Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget Amount ($)</label>
              <input
                type="number"
                value={content.budget || ''}
                onChange={(e) => onChange('budget', parseFloat(e.target.value) || 0)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                min="10"
                step="10"
                placeholder="Enter budget amount"
              />
            </div>

            {/* Bid Strategy */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bid Strategy</label>
              <select
                value={content.bidStrategy || 'auto'}
                onChange={(e) => onChange('bidStrategy', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="auto">Automated Bidding</option>
                <option value="manual_cpc">Manual CPC</option>
                <option value="manual_cpm">Manual CPM</option>
                <option value="max_conversions">Maximize Conversions</option>
              </select>
            </div>

            {/* Schedule */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Schedule</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={content.startDate || ''}
                    onChange={(e) => onChange('startDate', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">End Date</label>
                  <input
                    type="date"
                    value={content.endDate || ''}
                    onChange={(e) => onChange('endDate', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Monthly Spend Estimate */}
            {content.budget && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">Estimated Monthly Spend</h4>
                <p className="text-2xl font-bold text-blue-700">
                  ${content.budgetType === 'daily'
                    ? (content.budget * 30.4).toFixed(2)
                    : (content.budget / 1).toFixed(2)
                  }
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {content.budgetType === 'daily'
                    ? `Based on $${content.budget}/day × 30.4 days`
                    : `Total campaign budget`
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}